import { useCallback, useEffect, useState } from 'react';
import { supabase, type Profile } from '@/lib/supabase';

export default function OwnerStudentApproval({ academyId }: { academyId: string }) {
  const [pending, setPending] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'student')
        .eq('status', 'pending')
        .eq('academy_id', academyId);
      if (err) throw err;
      setPending((data as Profile[]) || []);
    } catch (e) {
      setError('승인 대기 학생을 불러오지 못했습니다.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [academyId]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id: string) => {
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase.rpc('approve_student', { p_profile_id: id });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('승인 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const reject = async (id: string) => {
    setBusy(true);
    setError('');
    try {
      const { error: err } = await supabase.rpc('reject_student', { p_profile_id: id });
      if (err) throw err;
      await load();
    } catch (e) {
      setError('거절 처리 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-background-200">
      <div className="p-4 border-b border-background-100 flex items-center gap-2">
        <h3 className="font-semibold text-foreground-950 text-sm flex items-center gap-2">
          <i className="ri-user-add-line text-accent-500"></i>
          학생 가입 승인
        </h3>
        {pending.length > 0 && (
          <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full">
            {pending.length}
          </span>
        )}
      </div>

      {error && (
        <div className="mx-4 mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="divide-y divide-background-100">
        {pending.length === 0 ? (
          <div className="p-5 text-center text-foreground-500 text-sm">
            승인 대기 중인 학생 가입 요청이 없습니다.
          </div>
        ) : (
          pending.map((s) => (
            <div key={s.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-foreground-950 text-sm">
                    {s.full_name || '이름 없음'}
                  </span>
                  {s.grade && (
                    <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full">
                      {s.grade}
                    </span>
                  )}
                  <span className="text-xs text-foreground-500">{s.address || '지역 미기입'}</span>
                </div>
                <div className="text-sm text-foreground-600 mt-0.5">
                  {s.academy_name || '학원명 미기입'}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => approve(s.id)}
                  disabled={busy}
                  className="whitespace-nowrap bg-accent-500 hover:bg-accent-600 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                >
                  승인
                </button>
                <button
                  onClick={() => reject(s.id)}
                  disabled={busy}
                  className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 text-xs font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer disabled:opacity-60"
                >
                  거절
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}